import { FileText, File, FileVideo, FileAudio, FileCode, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/modern/utils';

interface FilePreviewFile {
  name?: string;
  thumbnailUrl?: string;
  originalImageUrl?: string;
  isCCImage?: boolean;
}

interface FilePreviewProps {
  file: FilePreviewFile;
  className?: string;
  src?: string;
  showName?: boolean;
  openFileOnClick?: boolean;
  fileIconClassName?: string;
  fileIconContainerClassName?: string;
  disableLoadingState?: boolean; // Disable internal loading animation
}

const FilePreview = ({
  file,
  className = '',
  src,
  showName = false,
  openFileOnClick = false,
  fileIconClassName = '',
  fileIconContainerClassName = '',
  disableLoadingState = false,
  ...props
}: FilePreviewProps & React.HTMLAttributes<HTMLDivElement>) => {
  const [isLoading, setIsLoading] = useState(!disableLoadingState);

  const getFileExtension = (filename?: string): string => {
    if (!filename || typeof filename !== 'string') return '';
    if (filename.includes('localhost')) return 'jpg';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const getFileIcon = (filename?: string) => {
    const ext = getFileExtension(filename);
    const iconCls = cn('size-12', fileIconClassName);
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(ext)) {
      return null; // Show actual image
    }

    // Document files
    if (['pdf'].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md'].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Spreadsheet files
    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
      return <FileSpreadsheet className={iconCls} />;
    }

    // Presentation files
    if (['ppt', 'pptx', 'odp'].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Video files
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v'].includes(ext)) {
      return <FileVideo className={iconCls} />;
    }

    // Audio files
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma'].includes(ext)) {
      return <FileAudio className={iconCls} />;
    }

    // Code files
    if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'scss', 'json', 'xml', 'py', 'java', 'c', 'cpp', 'h', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt'].includes(ext)) {
      return <FileCode className={iconCls} />;
    }

    // Archive files
    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(ext)) {
      return <File className={iconCls} />;
    }

    // Default file icon
    return <File className={iconCls} />;
  };

  // CompanyCam images are always images, regardless of URL extension
  const icon = file.isCCImage ? null : getFileIcon(file.name);
  const isImage = !icon;

  const handleFileClick = () => {
    if (!openFileOnClick) return;
    const fileUrl = file.originalImageUrl || file.thumbnailUrl || src;
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  if (isImage) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video" />}
        <img
          src={src || file.thumbnailUrl || file.originalImageUrl}
          alt={file.name}
          className={cn("rounded-md", className, !disableLoadingState && isLoading && " hidden ", "cursor-pointer")}
          onClick={handleFileClick}
          onLoad={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onError={(e) => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
            // On error, replace with file icon
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const iconContainer = document.createElement('div');
            iconContainer.className = cn('w-full h-full flex flex-col items-center justify-center opacity-50 cursor-pointer ', fileIconContainerClassName);
            iconContainer.innerHTML = `<svg class="size-12 text-foreground ${fileIconClassName}" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>`;
            iconContainer.onclick = handleFileClick;

            if (showName) {
              const nameContainer = document.createElement('div');
              nameContainer.className = cn('text-sm text-center mt-2 px-2 break-words max-w-full');
              nameContainer.innerHTML = file.name || '';
              iconContainer.appendChild(nameContainer);
            }

            target.parentElement?.appendChild(iconContainer);
          }}
          {...props}
        />
      </>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center text-foreground ${!isImage && ` opacity-50 ${fileIconContainerClassName}`} ${className} cursor-pointer`}
      onClick={handleFileClick}
      {...props}
    >
      {icon}
      {!isImage && showName && file?.name && <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{file.name}</span>}
    </div>
  );
};

export default FilePreview;
